import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import * as authApi from '@/api/auth';
import {
  clearAuth,
  getEmailFromIdToken,
  isAccessTokenFresh,
  isPersistentAuth,
  loadAuth,
  saveAuth,
  saveRememberedEmail,
  saveRememberMePref,
  type StoredAuth,
} from '@/auth/storage';
import { ApiError } from '@/api/client';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  sessionId: string | null;
  email: string | null;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function restorePersistedAuth(): Promise<StoredAuth | null> {
  const stored = loadAuth();
  if (!stored?.accessToken) return null;

  if (isAccessTokenFresh(stored.accessToken)) return stored;

  if (!stored.refreshToken) {
    clearAuth();
    return null;
  }

  try {
    const tokens = await authApi.refreshToken(stored.refreshToken, stored.sessionId);
    const next: StoredAuth = {
      ...stored,
      ...tokens,
      sessionId: tokens.sessionId ?? stored.sessionId,
      email: getEmailFromIdToken(tokens.idToken) ?? stored.email,
    };
    saveAuth(next, isPersistentAuth());
    return next;
  } catch {
    clearAuth();
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<StoredAuth | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void restorePersistedAuth().then((next) => {
      if (cancelled) return;
      setAuth(next);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string, rememberMe = false) => {
    const tokens = await authApi.signIn({ email, password });
    const next: StoredAuth = {
      ...tokens,
      email: getEmailFromIdToken(tokens.idToken) ?? email,
    };
    saveRememberMePref(rememberMe);
    saveRememberedEmail(rememberMe ? email : null);
    saveAuth(next, rememberMe);
    setAuth(next);
  }, []);

  const signOut = useCallback(async () => {
    const current = loadAuth();
    if (current?.accessToken) {
      try {
        await authApi.signOut(current.accessToken, current.sessionId);
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 401) {
          console.warn('Sign out request failed', error);
        }
      }
    }
    clearAuth();
    setAuth(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: Boolean(auth?.accessToken),
      isLoading,
      accessToken: auth?.accessToken ?? null,
      sessionId: auth?.sessionId ?? null,
      email: auth?.email ?? getEmailFromIdToken(auth?.idToken) ?? null,
      signIn,
      signOut,
    }),
    [auth, isLoading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
